import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  filter,
  forkJoin,
  groupBy,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';

import { DrugService } from '../../../drugs/drugs.service';
import { MarketAnalysisResult } from '../../../drugs/models/drug-analysis.model';
import { DrugSearchLevel } from '../../../drugs/models/drug-grouping-level.model';
import { LinkService } from '../../../linking/link.service';
import { HospitalProductAnalysisSettingsService } from '../../../services/hospital-product-analysis-settings.service';
import { ProductAnalysisSettingsService } from '../../../services/product-analysis-settings.service';
import { CompetitorChartData, CompetitorSubMarket } from '../../models/competitor-chart-data.model';
import { Competitor } from '../../models/competitor.model';
import { PriceDevelopmentChartData } from '../../models/price-development-chart-data.model';
import { ProductAnalysisSettings } from '../../models/product-analysis-settings.model';
import { Product } from '../../models/product.model';
import { PurchaseProduct } from '../../models/purchase-product.model';
import { ProductService } from '../../products.service';
import * as ProductOverviewActions from './product-overview.actions';
import { selectChartPeriodMonths, selectProductId } from './product-overview.selectors';
import { ProductOverviewService } from './product-overview.service';

export const loadPriceDevelopment$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    productOverviewService = inject(ProductOverviewService)
  ) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.loadPriceDevelopment, ProductOverviewActions.setChartPeriod),
      withLatestFrom(store.select(selectProductId), store.select(selectChartPeriodMonths)),
      filter(([, productId]) => productId != null),
      switchMap(([, productId, periodMonths]) =>
        productOverviewService.getPriceDevelopment(productId!, periodMonths).pipe(
          map((data: PriceDevelopmentChartData) =>
            ProductOverviewActions.loadPriceDevelopmentSuccess({ data })
          ),
          catchError(error =>
            of(
              ProductOverviewActions.loadPriceDevelopmentFailure({
                error: error?.message ?? 'Unknown error loading price development',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// Each sub-market is its own request, so primary and hospital load in parallel.
export const loadSalesDevelopment$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    productOverviewService = inject(ProductOverviewService)
  ) => {
    function fetchSalesDevelopment(
      productId: string,
      subMarket: CompetitorSubMarket,
      periodMonths: number
    ): Observable<Action> {
      return productOverviewService.getSalesDevelopment(productId, subMarket, periodMonths).pipe(
        map((data: CompetitorChartData) =>
          ProductOverviewActions.loadSalesDevelopmentSuccess({ subMarket, data })
        ),
        catchError(error =>
          of(
            ProductOverviewActions.loadSalesDevelopmentFailure({
              subMarket,
              error: error?.message ?? 'Unknown error loading sales development',
            })
          )
        )
      );
    }

    return actions$.pipe(
      ofType(ProductOverviewActions.loadSalesDevelopment, ProductOverviewActions.setChartPeriod),
      withLatestFrom(store.select(selectProductId), store.select(selectChartPeriodMonths)),
      filter(([, productId]) => productId != null),
      // A period change reloads every sub-market, a load action only its own.
      mergeMap(([action, productId, periodMonths]) =>
        ('subMarket' in action
          ? [action.subMarket]
          : [CompetitorSubMarket.Primary, CompetitorSubMarket.Hospital]
        ).map(subMarket => ({ productId: productId!, subMarket, periodMonths }))
      ),
      // switchMap per sub-market, so a newer request cancels an older one for the same chart.
      groupBy(request => request.subMarket),
      mergeMap(requests$ =>
        requests$.pipe(
          switchMap(({ productId, subMarket, periodMonths }) =>
            fetchSalesDevelopment(productId, subMarket, periodMonths)
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadCompetitors$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), productOverviewService = inject(ProductOverviewService)) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.loadCompetitors),
      switchMap(({ productId }) =>
        productOverviewService.getCompetitors(productId).pipe(
          map((competitors: Competitor[]) =>
            ProductOverviewActions.loadCompetitorsSuccess({ competitors })
          ),
          catchError(error =>
            of(
              ProductOverviewActions.loadCompetitorsFailure({
                error: error?.message ?? 'Unknown error loading competitors',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// Not tied to the chart period, so it only loads once per product.
export const loadIpgSummary$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), drugService = inject(DrugService)) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.loadIpgSummary),
      switchMap(({ productId }) =>
        drugService.getIpgSummary(productId, DrugSearchLevel.InterchangePackSizeGroup).pipe(
          map((results: MarketAnalysisResult[]) =>
            ProductOverviewActions.loadIpgSummarySuccess({ results })
          ),
          catchError(error =>
            of(
              ProductOverviewActions.loadIpgSummaryFailure({
                error: error?.message ?? 'Unknown error loading IPG summary',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// switchMap per sub-market, so primary and hospital load in parallel and a newer request
// only cancels an older one for the same sub-market.
export const loadOverviewPurchaseProducts$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), productOverviewService = inject(ProductOverviewService)) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.loadPurchaseProducts),
      groupBy(({ subMarket }) => subMarket),
      mergeMap(requests$ =>
        requests$.pipe(
          switchMap(({ productId, subMarket }) =>
            productOverviewService.getPurchaseProducts(productId, subMarket).pipe(
              map((products: PurchaseProduct[]) =>
                ProductOverviewActions.loadPurchaseProductsSuccess({ subMarket, products })
              ),
              catchError(error =>
                of(
                  ProductOverviewActions.loadPurchaseProductsFailure({
                    subMarket,
                    error: error?.message ?? 'Unknown error loading purchase products',
                  })
                )
              )
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadOverviewProduct$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), productService = inject(ProductService)) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.loadProduct),
      switchMap(({ productId }) =>
        productService.getById(productId).pipe(
          map((product: Product) => ProductOverviewActions.loadProductSuccess({ product })),
          catchError(error =>
            of(
              ProductOverviewActions.loadProductFailure({
                error: error?.message ?? 'Unknown error loading product',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadAnalysisSettings$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    productAnalysisSettingsService = inject(ProductAnalysisSettingsService)
  ) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.loadAnalysisSettings),
      switchMap(({ productId }) =>
        productAnalysisSettingsService.getSettings(productId).pipe(
          map((settings: ProductAnalysisSettings) =>
            ProductOverviewActions.loadAnalysisSettingsSuccess({ settings })
          ),
          catchError(error =>
            of(
              ProductOverviewActions.loadAnalysisSettingsFailure({
                error: error?.message ?? 'Unknown error loading analysis settings',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const saveAnalysisSettings$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    productAnalysisSettingsService = inject(ProductAnalysisSettingsService)
  ) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.saveAnalysisSettings),
      concatMap(({ settings }) =>
        productAnalysisSettingsService.updateSettings(settings).pipe(
          map(() =>
            ProductOverviewActions.saveAnalysisSettingsSuccess({ productId: settings.productId })
          ),
          catchError(error =>
            of(
              ProductOverviewActions.saveAnalysisSettingsFailure({
                error: error?.message ?? 'Unknown error saving analysis settings',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const saveHospitalSettings$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    hospitalSettingsService = inject(HospitalProductAnalysisSettingsService)
  ) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.saveHospitalSettings),
      concatMap(({ settings }) =>
        hospitalSettingsService.updateSettings(settings).pipe(
          map(() =>
            ProductOverviewActions.saveHospitalSettingsSuccess({ productId: settings.productId })
          ),
          catchError(error =>
            of(
              ProductOverviewActions.saveHospitalSettingsFailure({
                error: error?.message ?? 'Unknown error saving hospital settings',
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// Updates and deletes are sent together, so the purchase products only reload once.
// Errors are caught per request, so both always finish and a partial failure still reloads.
export const changePurchaseProductLinks$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), linkService = inject(LinkService)) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.changePurchaseProductLinks),
      concatMap(({ productId, updates, deletes }) => {
        const requests: Observable<void>[] = [];
        if (updates.length) {
          requests.push(linkService.updateLinkStates(updates));
        }
        if (deletes.length) {
          requests.push(linkService.deleteLinks(deletes));
        }
        return forkJoin(
          requests.map(request =>
            request.pipe(
              map((): string | null => null),
              catchError(error =>
                of<string | null>(error?.message ?? 'Unknown error changing link states')
              )
            )
          )
        ).pipe(
          map((results: (string | null)[]) => {
            const error: string | null | undefined = results.find(result => result !== null);
            return error
              ? ProductOverviewActions.changePurchaseProductLinksFailure({ productId, error })
              : ProductOverviewActions.changePurchaseProductLinksSuccess({ productId });
          })
        );
      })
    );
  },
  { functional: true }
);

// Supplier prices, link states and analysis settings feed the purchase products of both
// sub-markets; hospital settings only feed the hospital ones.
export const reloadPurchaseProducts$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        ProductOverviewActions.saveAnalysisSettingsSuccess,
        ProductOverviewActions.saveHospitalSettingsSuccess,
        ProductOverviewActions.changePurchaseProductLinksSuccess,
        // Part of the change may have been saved.
        ProductOverviewActions.changePurchaseProductLinksFailure
      ),
      mergeMap(action =>
        (action.type === ProductOverviewActions.saveHospitalSettingsSuccess.type
          ? [CompetitorSubMarket.Hospital]
          : [CompetitorSubMarket.Primary, CompetitorSubMarket.Hospital]
        ).map(subMarket =>
          ProductOverviewActions.loadPurchaseProducts({ productId: action.productId, subMarket })
        )
      )
    );
  },
  { functional: true }
);

// The table shows the saved market shares.
export const reloadAnalysisSettings$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ProductOverviewActions.saveAnalysisSettingsSuccess),
      map(({ productId }) => ProductOverviewActions.loadAnalysisSettings({ productId }))
    );
  },
  { functional: true }
);

export const productOverviewEffects: object = {
  changePurchaseProductLinks$,
  loadAnalysisSettings$,
  loadCompetitors$,
  loadIpgSummary$,
  loadOverviewProduct$,
  loadPriceDevelopment$,
  loadOverviewPurchaseProducts$,
  loadSalesDevelopment$,
  reloadAnalysisSettings$,
  reloadPurchaseProducts$,
  saveAnalysisSettings$,
  saveHospitalSettings$,
};
